/* Help persistence fix, 3 October 2026. No storage, network or checkout changes. */
(() => {
  'use strict';
  if (customElements.get('phq-mobile-contact')) return;
  const overlays = 'dialog[open],[aria-modal="true"],#Details-menu-drawer-container[open],.menu-drawer-container.menu-open,#shopify-pc__prefs,#shopify-pc__prefs__dialog';
  const visible = el => {
    const rect = el.getBoundingClientRect();
    const style = getComputedStyle(el);
    return rect.width > 0 && rect.height > 0 && style.visibility !== 'hidden' && style.display !== 'none' && !el.closest('[hidden],[inert]');
  };
  const clippedPreviewRect = el => {
    const rect = el.getBoundingClientRect();
    let top = Math.max(0, rect.top), bottom = Math.min(innerHeight, rect.bottom);
    for (let parent = el.parentElement; parent && parent !== document.body; parent = parent.parentElement) {
      if (/hidden|clip|auto|scroll/.test(getComputedStyle(parent).overflowY)) {
        const clip = parent.getBoundingClientRect();
        top = Math.max(top, clip.top); bottom = Math.min(bottom, clip.bottom);
      }
    }
    return { top, bottom };
  };
  class PrepareHQMobileContact extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.toggle = this.querySelector('button');
      this.panel = this.querySelector('nav');
      this.label = this.querySelector('[data-phq-help-label]');
      if (!this.toggle || !this.panel || !this.label) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      const on = (target, name, fn, options = {}) => target.addEventListener(name, fn, { ...options, signal });
      this.schedule = () => {
        if (!this.frame) this.frame = requestAnimationFrame(() => { this.frame = 0; this.update(); });
      };
      on(this.toggle, 'click', () => this.setOpen(this.panel.hidden));
      on(this, 'keydown', e => {
        if (e.key === 'Escape' && !this.panel.hidden) {
          e.preventDefault(); e.stopPropagation(); this.setOpen(false); this.toggle.focus({ preventScroll: true });
        }
      });
      on(document, 'pointerdown', e => { if (!e.composedPath().includes(this)) this.setOpen(false); }, { capture: true });
      on(this, 'focusout', e => { if (e.relatedTarget && !this.contains(e.relatedTarget)) this.setOpen(false); });
      on(document, 'focusin', this.schedule);
      on(document, 'focusout', this.schedule);
      // Scrolling never hides Help. Recheck only overlays, keyboard and preview-bar offset.
      on(window, 'scroll', this.schedule, { passive: true });
      on(window, 'resize', this.schedule, { passive: true });
      on(window, 'pageshow', () => { this.setOpen(false); this.schedule(); });
      on(document, 'toggle', this.schedule, { capture: true });
      on(document, 'dialog:open', this.schedule);
      on(document, 'dialog:close', this.schedule);
      if (window.visualViewport) {
        on(window.visualViewport, 'resize', this.schedule, { passive: true });
        on(window.visualViewport, 'scroll', this.schedule, { passive: true });
      }
      this.observer = new MutationObserver(records => {
        // Ignore our own style/state writes, including writes to this host.
        if (records.some(record => !this.contains(record.target))) this.schedule();
      });
      this.observer.observe(document.body, { subtree: true, childList: true, attributes: true,
        attributeFilter: ['open','hidden','aria-hidden','aria-modal','class','style','inert'] });
      this.hidden = false;
      this.setOpen(false);
      this.update();
    }
    disconnectedCallback() {
      this.controller?.abort(); this.observer?.disconnect();
      cancelAnimationFrame(this.frame);
      this.frame = 0; this.controller = null;
    }
    setOpen(open) {
      if (open && this.blocked) return;
      this.panel.hidden = !open;
      this.toggle.setAttribute('aria-expanded', String(open));
      this.label.textContent = open ? 'Close help' : 'Help';
    }
    update() {
      if (!this.isConnected) return;
      let blocked = false;
      try {
      const active = document.activeElement;
      const viewport = window.visualViewport;
      const editing = active?.matches('textarea,[contenteditable]:not([contenteditable="false"]),input:not([type="hidden"]):not([type="radio"]):not([type="checkbox"]):not([type="button"]):not([type="submit"]):not([type="range"]):not([type="color"])') && !this.contains(active);
      // Normalise pinch zoom; short landscape viewports and focused radio/select controls stay usable.
      const keyboard = !!(editing && viewport && innerHeight - viewport.height * viewport.scale > 150);
      const overlay = [...document.querySelectorAll(overlays)].some(el => !this.contains(el) && visible(el));
      const previewFrames = [...document.querySelectorAll('iframe')].filter(el => /preview[-_ ]?bar/i.test(el.id + ' ' + el.title + ' ' + (el.getAttribute('src') || '')) && visible(el));
      const bottomBars = [...previewFrames, ...document.querySelectorAll('#shopify-pc__banner')].filter(visible);
      const launcherRect = this.toggle.getBoundingClientRect();
      const previewHeight = bottomBars.reduce((height, el) => {
        const { top, bottom } = clippedPreviewRect(el);
        const rect = el.getBoundingClientRect();
        const overlapsHorizontally = rect.right > launcherRect.left && rect.left < launcherRect.right;
        const atBottom = el.id === 'shopify-pc__banner' ? bottom > innerHeight / 2 : bottom >= innerHeight - 2;
        const isBar = bottom - top <= innerHeight / 3;
        return overlapsHorizontally && bottom > top && atBottom && isBar ? Math.max(height, innerHeight - top) : height;
      }, 0);
      const offset = Math.min(Math.max(0, Math.ceil(previewHeight)), Math.round(innerHeight / 3)) + 'px';
      if (this.style.getPropertyValue('--phq-help-preview-offset') !== offset) this.style.setProperty('--phq-help-preview-offset', offset);
      blocked = keyboard || overlay;
      } catch (_) {
        // A detection failure must not leave Help stuck hidden after an overlay closes.
        this.style.removeProperty('--phq-help-preview-offset');
      }
      this.blocked = blocked;
      if (this.blocked) this.setOpen(false);
      this.toggle.setAttribute('aria-disabled', String(this.blocked));
      // An open external overlay owns focus. Nothing on the ordinary page can suppress Help.
      const active = document.activeElement;
      if (this.blocked && this.contains(active)) active.blur();
      this.toggleAttribute('data-suppressed', this.blocked);
      this.inert = this.blocked;
      if (this.blocked) this.setAttribute('aria-hidden', 'true'); else this.removeAttribute('aria-hidden');
    }
  }
  customElements.define('phq-mobile-contact', PrepareHQMobileContact);
})();
